import { supabase } from '@/lib/supabase';
import type { BudgetCategoryRow, ProjectBudgetRow, BudgetLineRow } from '@/types/database';

/** Designer-owned budget categories (migration 0020). */
export async function listBudgetCategories(): Promise<BudgetCategoryRow[]> {
  const { data, error } = await supabase
    .from('budget_categories')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function createBudgetCategory(name: string): Promise<BudgetCategoryRow> {
  const { data: session } = await supabase.auth.getUser();
  const designerId = session.user?.id;
  if (!designerId) throw new Error('Not signed in');
  const { data, error } = await supabase
    .from('budget_categories')
    .insert({ designer_id: designerId, name: name.trim(), sort_order: 100 })
    .select()
    .single();
  if (error) throw error;
  return data as BudgetCategoryRow;
}

export async function updateBudgetCategory(id: string, name: string): Promise<BudgetCategoryRow> {
  const { data, error } = await supabase
    .from('budget_categories')
    .update({ name: name.trim() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as BudgetCategoryRow;
}

/**
 * What a category is holding, ACROSS THE FIRM — budget categories are shared
 * by every project, so deleting one reaches further than the project you're
 * looking at.
 *
 * Materials and material categories point at it `on delete set null`: they
 * survive and roll up as Uncategorized. Budget LINES are `on delete cascade`
 * (0020): their manual and labor amounts go with the category, in every
 * project. That's what the warning has to say out loud.
 */
export async function budgetCategoryUsage(id: string): Promise<{
  projectCount: number; manualCents: number; laborCents: number;
  specCount: number; mappedCategoryCount: number;
}> {
  const [lines, specs, mapped] = await Promise.all([
    supabase.from('budget_lines').select('project_id, materials_manual_cents, labor_cents').eq('budget_category_id', id),
    supabase.from('specs').select('id', { count: 'exact', head: true }).eq('budget_category_id', id),
    supabase.from('material_categories').select('id', { count: 'exact', head: true }).eq('budget_category_id', id),
  ]);
  if (lines.error) throw lines.error;
  const rows = lines.data ?? [];
  return {
    projectCount: new Set(rows.map((r) => r.project_id)).size,
    manualCents: rows.reduce((t, r) => t + (r.materials_manual_cents ?? 0), 0),
    laborCents: rows.reduce((t, r) => t + (r.labor_cents ?? 0), 0),
    specCount: specs.count ?? 0,
    mappedCategoryCount: mapped.count ?? 0,
  };
}

export async function deleteBudgetCategory(id: string): Promise<void> {
  // See budgetCategoryUsage: materials survive as Uncategorized, but this
  // DOES delete the category's manual and labor amounts in every project.
  const { error } = await supabase.from('budget_categories').delete().eq('id', id);
  if (error) throw error;
}

/** The project's budget header. Null when the designer hasn't set one up yet. */
export async function getProjectBudget(projectId: string): Promise<ProjectBudgetRow | null> {
  const { data, error } = await supabase
    .from('project_budgets')
    .select('*')
    .eq('project_id', projectId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

/**
 * Create-or-update the budget header.
 *
 * Upsert rather than insert-then-update: the row is created lazily the first
 * time a designer types a number, so there's no separate "start a budget" step.
 */
export async function saveProjectBudget(
  projectId: string,
  patch: { designer_fee_cents?: number },
): Promise<void> {
  const { error } = await supabase
    .from('project_budgets')
    .upsert({ project_id: projectId, ...patch }, { onConflict: 'project_id' });
  if (error) throw error;
}

export async function listBudgetLines(projectId: string): Promise<BudgetLineRow[]> {
  const { data, error } = await supabase
    .from('budget_lines')
    .select('*')
    .eq('project_id', projectId);
  if (error) throw error;
  return data ?? [];
}

/** Create-or-update one category's manual amounts. */
export async function saveBudgetLine(
  projectId: string,
  budgetCategoryId: string,
  patch: { materials_manual_cents?: number; labor_cents?: number; notes?: string | null },
): Promise<void> {
  const { error } = await supabase
    .from('budget_lines')
    .upsert(
      { project_id: projectId, budget_category_id: budgetCategoryId, ...patch },
      { onConflict: 'project_id,budget_category_id' },
    );
  if (error) throw error;
}
